// Adopt a signature (or initials): type it in a handwriting font, or draw it. Produces a trimmed PNG data URL,
// which is the only thing a signer uploads (the server checks it's a real PNG under 300 KB).
import { useEffect, useRef, useState } from "react";
import { Eraser } from "lucide-react";
import { Button, cx, Modal } from "./ui";

const INK = "#1f2a6b";

export function SignaturePad({ kind, name, onAdopt, onClose }: { kind: "signature" | "initials"; name: string; onAdopt: (png: string) => void; onClose: () => void }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase())
    .join("")
    .slice(0, 4);
  const [mode, setMode] = useState<"type" | "draw">("type");
  const [text, setText] = useState(kind === "initials" ? initials : name);
  const [drawn, setDrawn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pad = useRef<HTMLCanvasElement>(null);
  const W = kind === "initials" ? 300 : 600;
  const H = 180;

  useEffect(() => {
    void document.fonts?.load(`64px Caveat`).catch(() => undefined);
  }, []);

  // Drawing
  useEffect(() => {
    const c = pad.current;
    if (mode !== "draw" || !c) return;
    const ctx = c.getContext("2d")!;
    ctx.lineWidth = 3.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = INK;
    let down = false;
    let last: [number, number] | null = null;
    const pos = (e: PointerEvent): [number, number] => {
      const r = c.getBoundingClientRect();
      return [((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H];
    };
    const start = (e: PointerEvent) => {
      down = true;
      try {
        c.setPointerCapture(e.pointerId);
      } catch {
        /* pointer already gone; drawing still works without capture */
      }
      last = pos(e);
      e.preventDefault();
    };
    const move = (e: PointerEvent) => {
      if (!down || !last) return;
      const p = pos(e);
      ctx.beginPath();
      ctx.moveTo(...last);
      ctx.lineTo(...p);
      ctx.stroke();
      last = p;
      setDrawn(true);
    };
    const end = () => {
      down = false;
      last = null;
    };
    c.addEventListener("pointerdown", start);
    c.addEventListener("pointermove", move);
    c.addEventListener("pointerup", end);
    c.addEventListener("pointercancel", end);
    return () => {
      c.removeEventListener("pointerdown", start);
      c.removeEventListener("pointermove", move);
      c.removeEventListener("pointerup", end);
      c.removeEventListener("pointercancel", end);
    };
  }, [mode, W]);

  const clear = () => {
    pad.current?.getContext("2d")?.clearRect(0, 0, W, H);
    setDrawn(false);
  };

  const adopt = async () => {
    setError(null);
    let source: HTMLCanvasElement;
    if (mode === "type") {
      if (!text.trim()) return setError(kind === "initials" ? "Type your initials" : "Type your name");
      await document.fonts?.load(`72px Caveat`).catch(() => undefined);
      source = document.createElement("canvas");
      source.width = W * 2;
      source.height = H;
      const ctx = source.getContext("2d")!;
      let size = 96;
      ctx.font = `700 ${size}px Caveat, "Segoe Script", cursive`;
      while (ctx.measureText(text).width > source.width - 40 && size > 30) {
        size -= 4;
        ctx.font = `700 ${size}px Caveat, "Segoe Script", cursive`;
      }
      ctx.fillStyle = INK;
      ctx.textBaseline = "middle";
      ctx.fillText(text.trim(), 20, H / 2);
    } else {
      if (!drawn || !pad.current) return setError("Draw your " + (kind === "initials" ? "initials" : "signature"));
      source = pad.current;
    }
    const png = trim(source);
    if (!png) return setError("That came out empty. Try again.");
    onAdopt(png);
  };

  return (
    <Modal
      title={kind === "initials" ? "Adopt your initials" : "Adopt your signature"}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={adopt}>
            Adopt and sign
          </Button>
        </>
      }
    >
      <div className="flex gap-1 rounded-lg bg-surface-2 p-1 w-fit mb-4" role="tablist">
        {(["type", "draw"] as const).map((m) => (
          <button key={m} role="tab" aria-selected={mode === m} onClick={() => setMode(m)} className={cx("px-3 h-8 rounded-md text-sm font-medium", mode === m ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink")}>
            {m === "type" ? "Type" : "Draw"}
          </button>
        ))}
      </div>
      {mode === "type" ? (
        <>
          <label className="block text-sm font-medium text-ink-2 mb-1" htmlFor="sig-text">
            {kind === "initials" ? "Initials" : "Full name"}
          </label>
          <input id="sig-text" value={text} maxLength={kind === "initials" ? 6 : 80} onChange={(e) => setText(e.target.value)} className="w-full h-10 rounded-lg border border-line bg-surface px-3 text-sm text-ink" />
          <div className="mt-4 rounded-lg border border-line bg-white h-28 flex items-center px-5 overflow-hidden">
            <span className="signature-font text-5xl font-bold truncate" style={{ color: INK }}>
              {text || " "}
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="relative rounded-lg border border-line bg-white">
            <canvas ref={pad} width={W} height={H} className="w-full touch-none cursor-crosshair" style={{ aspectRatio: `${W} / ${H}` }} aria-label="Draw here" />
            <div className="absolute left-5 right-5 bottom-8 border-b border-dashed border-black/20 pointer-events-none" />
            <Button variant="ghost" className="absolute top-2 right-2 h-8" onClick={clear}>
              <Eraser className="size-4" /> Clear
            </Button>
          </div>
          <p className="text-xs text-muted mt-2">Draw with your mouse, finger or pen.</p>
        </>
      )}
      {error && <p className="text-sm text-bad mt-3">{error}</p>}
      <p className="text-xs text-muted mt-4">By selecting Adopt and sign, I agree that this mark is my electronic {kind === "initials" ? "initials" : "signature"} and has the same effect as my handwritten one on this document.</p>
    </Modal>
  );
}

/** Crops transparent margins (keeping a little padding) and returns a PNG data URL, or null if empty. */
function trim(src: HTMLCanvasElement): string | null {
  const ctx = src.getContext("2d")!;
  const { width, height } = src;
  const data = ctx.getImageData(0, 0, width, height).data;
  let x0 = width,
    y0 = height,
    x1 = -1,
    y1 = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return null;
  const pad = 6;
  x0 = Math.max(0, x0 - pad);
  y0 = Math.max(0, y0 - pad);
  x1 = Math.min(width - 1, x1 + pad);
  y1 = Math.min(height - 1, y1 + pad);
  const out = document.createElement("canvas");
  out.width = x1 - x0 + 1;
  out.height = y1 - y0 + 1;
  out.getContext("2d")!.drawImage(src, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL("image/png");
}
